import React, { useEffect, useRef } from "react";
import { Button, Tooltip } from "antd";
import SpeechRecognition, { useSpeechRecognition } from "react-speech-recognition";
import { AudioMutedOutlined, AudioOutlined } from "@ant-design/icons";
const SpeechToTextButton = ({ index, isActive, onSpeech, onStart, onStop, disabled }) => {
	const {
		transcript,
		listening,
		resetTranscript,
		browserSupportsSpeechRecognition,
		isMicrophoneAvailable,
	} = useSpeechRecognition();
	const transcriptRef = useRef("");
	const silenceTimer = useRef(null);

	// 🔹 Track live speech and detect pause
	useEffect(() => {
		if (!isActive) return;
		transcriptRef.current = transcript;
		if (listening && transcript) {
			clearTimeout(silenceTimer.current);
			//  Detect 1s of silence → user paused speaking
			silenceTimer.current = setTimeout(() => {
				handlePartialSpeech();
			}, 1000);
		}

		return () => {
			clearTimeout(silenceTimer.current);
		}
	}, [transcript, listening, isActive]);
	// Stop mic when component unmounts
	useEffect(() => {
		return () => {
			if (listening) {
				SpeechRecognition.stopListening();
			}
			clearTimeout(silenceTimer.current);
			resetTranscript();
			transcriptRef.current = "";
		};
	}, [listening, resetTranscript]);

	// Insert partial speech into editor on pause
	const handlePartialSpeech = () => {
		const partialText = transcriptRef.current.trim();
		if (partialText && onSpeech) {
			let processedText = partialText;
			//  Add period if last char is not punctuation
			if (!/[.!?,]$/.test(partialText)) {
				processedText += ".";
			}

			onSpeech(processedText, index); // Insert into Quill editor
		}

		// Reset for next chunk
		resetTranscript();
		transcriptRef.current = "";
	};

	const startListening = () => {
		if (!browserSupportsSpeechRecognition) {
			alert("❌ Your browser doesn't support speech recognition.");
			return;
		}

		if (!isMicrophoneAvailable) {
			alert("🎤 Please allow microphone access in your browser.");
			return;
		}

		resetTranscript();
		transcriptRef.current = "";

		SpeechRecognition.startListening({
			continuous: true, // Keep listening until user clicks stop
			language: "en-US",
		});

		if (onStart) onStart(index);
	};

	const stopListening = () => {
		SpeechRecognition.stopListening();
		clearTimeout(silenceTimer.current);

		// Insert last pending chunk
		const finalText = transcriptRef.current.trim();
		if (finalText && onSpeech) {
			let processedText = finalText;
			if (!/[.!?,]$/.test(finalText)) processedText += ".";
			onSpeech(processedText, index);
		}

		resetTranscript();
		transcriptRef.current = "";

		if (onStop) onStop(index);
	};

	return (
		<div className="flex flex-col sm:flex-row sm:items-center gap-2">
			<Tooltip title={listening && isActive ? "Pause Listening" : "Start Talking"}>
				<Button
					type="default"
					shape="circle"
					size="middle"
					disabled={disabled}
					onClick={() => {
						if (listening && isActive) stopListening();
						else startListening();
					}}
					className={`transition-all duration-300 shadow-md flex items-center justify-center 
						${listening && isActive ? 
							"bg-red-600 hover:bg-red-700 text-white border-red-600 shadow-red-400 animate-pulse"
							: "bg-gray-200 hover:bg-gray-300 text-gray-700"
						}`}
					icon={listening && isActive ? <AudioOutlined className="text-xl" /> : <AudioMutedOutlined className="text-xl" />}
				/>
			</Tooltip>

			{isActive && listening && (
				<span className="italic text-gray-600 text-sm break-words">
					<span className="inline-block w-2 h-2 bg-red-500 rounded-full mr-2 animate-pulse"></span>
					Listening... {transcript}
				</span>
			)}
		</div>
	);
};

export default SpeechToTextButton;
